'use client';

import { useEffect, useState, useCallback, Suspense } from 'react';
import dynamic from 'next/dynamic';
import { LuPanelLeft } from 'react-icons/lu';
import { Button } from '@/core/ui/primitives/button';
import { Separator } from '@/core/ui/primitives/separator';
import { useSidebarOptional } from '@/core/ui/primitives/sidebar';
import { usePanelOptional } from '@/core/helpers/react/contexts/PanelContext';
import { useAppReadyOptional } from '@core/helpers/react/contexts/AppReadyContext';
import { ThemeToggle } from '@/core/ui/navigation/main-menu/ThemeToggle';
import { OrganizationDialog } from '@/core/ui/profile/organizations/OrganizationDialog';
import { PlatformConsoleDialog } from '@/core/ui/platform/PlatformConsoleDialog';
import { AppNavBar } from './AppNavBar';
import { NavBreadcrumb } from './NavBreadcrumb';
import { NavCenterContent } from './NavCenterContent';
import { OrganizationSelector } from './OrganizationSelector';
import { SearchButton } from './SearchButton';
import { UserAvatar } from './UserAvatar';
import { CollaborationPresenceHandle } from './CollaborationPresenceHandle';

const ProfileSettingsWindow = dynamic(() => import('@core/ui/profile/ProfileSettingsWindow'), {
  ssr: false,
});

// Layout width settings (Wide screens only: ≥1280px)
// Users can only increase from min(1440px, viewport) up to viewport width
const PREFERRED_LAYOUT_WIDTH = 1440;

interface AppNavProps {
  /** Content for the center zone (breadcrumb, navigation links, etc.) */
  centerContent?: React.ReactNode;
  /** Additional right-side content before the standard controls */
  additionalRightContent?: React.ReactNode;
}

/**
 * AppNav - The main application navigation component
 *
 * This is the global navigation bar used across the application.
 * It provides:
 * - Organization selector (left)
 * - Sidebar toggle (when inside SidebarProvider context)
 * - Center content slot (navigation, breadcrumb, etc.)
 * - Search, Text size, Width (Wide screens only), Theme toggle, User avatar (right)
 */
export function AppNav({ centerContent, additionalRightContent }: AppNavProps) {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profileInitialSection, setProfileInitialSection] = useState<string | undefined>(undefined);
  const [isOrganizationOpen, setIsOrganizationOpen] = useState(false);
  const [isPlatformConsoleOpen, setIsPlatformConsoleOpen] = useState(false);
  const appReady = useAppReadyOptional();

  // Get sidebar context if available (null when outside SidebarProvider)
  const sidebarContext = useSidebarOptional();
  const panelContext = usePanelOptional();

  // Signal nav ready to app ready context
  useEffect(() => {
    appReady?.signalReady('nav');
  }, [appReady]);

  // Global settings state with localStorage persistence
  const [fontScale, setFontScale] = useState(100);
  const [layoutWidth, setLayoutWidth] = useState(() => {
    if (typeof window === 'undefined') return PREFERRED_LAYOUT_WIDTH;
    return Math.min(PREFERRED_LAYOUT_WIDTH, window.innerWidth);
  });

  // Sidebar toggle is available when we're inside a SidebarProvider AND there's content (leftPanel or navMenu)
  const hasSidebar = !!sidebarContext && !!(panelContext?.leftPanel || panelContext?.navMenu);
  const sidebarOpen = sidebarContext?.open ?? false;
  const handleSidebarToggle = useCallback(() => {
    sidebarContext?.toggleSidebar();
  }, [sidebarContext]);

  // Load persisted settings on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const storedFontScale = window.localStorage.getItem('app:fontScale');
      if (storedFontScale) {
        const parsed = parseInt(storedFontScale, 10);
        if (parsed >= 80 && parsed <= 150) {
          setFontScale(parsed);
        }
      }
      const storedWidth = window.localStorage.getItem('app:layoutWidth');
      if (storedWidth) {
        const parsed = parseInt(storedWidth, 10);
        const minWidth = Math.min(PREFERRED_LAYOUT_WIDTH, window.innerWidth);
        const maxWidth = window.innerWidth;
        if (parsed >= minWidth && parsed <= maxWidth) {
          setLayoutWidth(parsed);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  // Persist font scale and update CSS variable
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('app:fontScale', String(fontScale));
      // Set the CSS variable used by vault markdown content (value is a multiplier, e.g., 1.0 for 100%)
      document.documentElement.style.setProperty('--vault-markdown-font-scale', String(fontScale / 100));
    } catch {
      // ignore
    }
  }, [fontScale]);

  // Persist layout width and update CSS variable
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('app:layoutWidth', String(layoutWidth));
      document.documentElement.style.setProperty('--app-layout-width', `${layoutWidth}px`);
    } catch {
      // ignore
    }
  }, [layoutWidth]);

  // Listen for profile/organization/platform dialog open events
  useEffect(() => {
    const onOpenProfileSettings = (e: Event) => {
      const section = (e as CustomEvent<{ section?: string }>)?.detail?.section;
      setProfileInitialSection(section);
      setIsProfileOpen(true);
    };
    const onOpenOrganizationSettings = () => setIsOrganizationOpen(true);
    const onOpenPlatformConsole = () => setIsPlatformConsoleOpen(true);

    window.addEventListener('profile.settings.open', onOpenProfileSettings);
    window.addEventListener('organization.settings.open', onOpenOrganizationSettings);
    window.addEventListener('platform.console.open', onOpenPlatformConsole);

    return () => {
      window.removeEventListener('profile.settings.open', onOpenProfileSettings);
      window.removeEventListener('organization.settings.open', onOpenOrganizationSettings);
      window.removeEventListener('platform.console.open', onOpenPlatformConsole);
    };
  }, []);

  return (
    <>
      <AppNavBar
        leftContent={
          <>
            {/* Sidebar toggle - always rendered to preserve layout, invisible when no sidebar */}
            <Button
              variant='ghost'
              size='icon'
              className={`h-9 w-9 ${!hasSidebar ? 'invisible pointer-events-none' : ''}`}
              onClick={handleSidebarToggle}
              aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
              disabled={!hasSidebar}>
              <LuPanelLeft className={`h-5 w-5 transition-transform ${sidebarOpen ? '' : 'rotate-180'}`} />
            </Button>
            <Separator orientation='vertical' className={`h-6 mx-1 ${!hasSidebar ? 'invisible' : ''}`} />
            {/* Organization selector - handles loading/unauthenticated state internally */}
            <OrganizationSelector />
            {/* Document breadcrumb - appears when scrolled past header in vault view */}
            <Suspense fallback={null}>
              <NavBreadcrumb />
            </Suspense>
          </>
        }
        centerContent={
          <Suspense fallback={null}>
            {centerContent ?? <NavCenterContent />}
          </Suspense>
        }
        rightContent={
          <div className='relative flex h-full items-center gap-1'>
            {additionalRightContent}
            <SearchButton />
            <Separator orientation='vertical' className='h-6 mx-1' />
            <ThemeToggle className='h-9 w-9' />
            <UserAvatar
              fontScale={fontScale}
              layoutWidth={layoutWidth}
              onFontScaleChange={setFontScale}
              onLayoutWidthChange={setLayoutWidth}
            />
            <CollaborationPresenceHandle />
          </div>
        }
      />

      {/* Dialogs */}
      <ProfileSettingsWindow
        open={isProfileOpen}
        onOpenChange={setIsProfileOpen}
        initialSection={profileInitialSection}
      />
      <OrganizationDialog open={isOrganizationOpen} onOpenChange={setIsOrganizationOpen} />
      <PlatformConsoleDialog open={isPlatformConsoleOpen} onOpenChange={setIsPlatformConsoleOpen} />
    </>
  );
}

export default AppNav;
